import type {
  AlgoliaLink,
  AlgoliaProduct,
} from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import type { Location } from '@ds/types/Location'

export interface Link {
  highlightedTitle: string | undefined
  title: string
  id?: string
  url: string | Location
}

export function algoliaHitToVariant(hit: AlgoliaProduct): Product {
  return {
    availableStock: hit.stockRaw,
    brand: hit.brand,
    brandClean: hit.brand_clean,
    categoryId: hit.cat_id,
    category: hit.category_3,
    description: hit.sub_title_elements?.join(' - ') ?? '',
    grade: {
      name: hit.backbox_grade_label,
      value: hit.backbox_grade_value,
    },
    id: hit.id,
    objectId: hit.objectID,
    image: hit.image1,
    listingId: `${hit.listingID}`,
    model: hit.model,
    modelClean: hit.model_clean,
    price: { amount: `${hit.price}`, currency: hit.currency },
    priceNew: hit.referencePrice,
    productId: String(hit.backmarketID),
    productPageLink: hit.link_grade_v2,
    reviewRating: hit.reviewRating,
    sellerId: `${hit.merchant_id}`,
    title: hit.title_model,
    warrantyDuration: hit.warranty,
  }
}

export function algoliaHitToProduct(hit: AlgoliaProduct): Product {
  const specEntries = Object.entries(hit.variant_fields).map(
    ([key, specification]) => {
      const cleanSpec = specification
        .map((val) => {
          return typeof val === 'string' ? { name: val } : val
        })
        .filter((item) => item.name)

      return [key, cleanSpec]
    },
  )
  const specifications = Object.fromEntries(specEntries)

  return {
    id: hit.id,
    objectId: hit.objectID,
    title: hit.title_model,
    listingId: `${hit.listingID}`,
    model: hit.model,
    modelClean: hit.model_clean,
    brand: hit.brand,
    brandClean: hit.brand_clean,
    sellerId: `${hit.merchant_id}`,
    categoryId: hit.cat_id,
    category: hit.category_3,
    grade: {
      name: hit.backbox_grade_label,
      value: hit.backbox_grade_value,
    },
    productId: String(hit.backmarketID),
    productPageLink: hit.link_master_pp ?? hit.link_grade_v2,
    priceNew: hit.referencePrice,
    price: {
      amount: `${hit.price}`,
      currency: hit.currency,
    },
    reviewRating: hit.reviewRating,
    availableStock: hit.stockRaw,
    warrantyDuration: hit.warranty,
    image: hit.image1,
    specifications,
    flashSalesDiscount: hit.flash_sales_discount ?? undefined,
    scores: {
      lifeExpectancy: hit.life_expectancy_score,
      performances: hit.performances_score,
      camera: hit.camera_score,
      screenQuality: hit.screen_quality_score,
      soundQuality: hit.sound_quality_score,
      multimedia: hit.multimedia_score,
    },
    marketingTags: hit.marketing_tags,
  }
}

export function algoliaHitToLink(hit: AlgoliaLink): Link {
  return {
    // eslint-disable-next-line no-underscore-dangle
    highlightedTitle: hit._highlightResult?.title.value,
    title: hit.title,
    ...(hit.link.type === 'external'
      ? {
          url: hit.link.href,
        }
      : {
          url: `/${hit.link.params.locale}/l/${hit.link.params.slugV2}/${hit.link.params.uuid}`,
          id: hit.link.params?.uuid,
        }),
  }
}
